import { ADDRESS_ERROR_MESSAGES } from "@src/appV2/Address/constants";
import { useWorkerAddress } from "@src/appV2/Address/hooks/useWorkerAddress";
import { isSupportedCountryCode, isValidPlace } from "@src/appV2/Address/utils/validation";
import { useToast } from "@src/appV2/lib";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
import { useGetWorker } from "@src/appV2/Worker/api/useGetWorker";
import { useMutation } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import timezoneLookup from "tz-lookup";

import { useUpdateOnboardingWorker } from "../../../api/useUpdateOnboardingWorker";
import { ONBOARDING_STAGES } from "../../../util/constants";
import { SHOULD_SHOW_EDIT_ADDRESS_QUERY_PARAM } from "../constants";

interface UseAddressStepProps {
  onNext: () => void | Promise<void>;
}

export function useAddressStep(props: UseAddressStepProps) {
  const { onNext } = props;

  const { showErrorToast } = useToast();

  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const shouldShowEditAddress = queryParams.get(SHOULD_SHOW_EDIT_ADDRESS_QUERY_PARAM) === "true";

  const {
    place,
    loadingData,
    handleUseCurrentLocation,
    addressInputModalState,
    addressInput,
    handleAddressInputChange,
    handlePlaceChange,
    handleManualEntry,
    addressInputErrorMessage,
    isEnablingGeoLocation,
  } = useWorkerAddress({
    shouldShowEditAddress,
  });

  const {
    data: worker,
    isLoading: isLoadingWorker,
    refetch: refetchWorker,
  } = useGetWorker({ enabled: false });

  const { mutateAsync: updateOnboardingWorker } = useUpdateOnboardingWorker();

  const { mutateAsync: updateOnboardingAddress, isLoading: isUpdatingOnboardingAddress } =
    useMutation({
      mutationKey: ["update-onboarding-address"],
      mutationFn: async () => {
        if (!isValidPlace(place)) {
          showErrorToast(ADDRESS_ERROR_MESSAGES.INVALID_ADDRESS);
          return;
        }

        if (!isSupportedCountryCode(place.address.countryCode)) {
          showErrorToast(ADDRESS_ERROR_MESSAGES.US_ONLY);

          logError(APP_V2_APP_EVENTS.ONBOARDING_UPDATE_ADDRESS_ERROR, {
            error: new Error(ADDRESS_ERROR_MESSAGES.US_ONLY),
            metadata: {
              workerId: worker?.userId,
            },
          });
          return;
        }

        let tmz: string | undefined;
        if (place.location.lat && place.location.lng) {
          tmz = timezoneLookup(place.location.lat, place.location.lng);
        }

        try {
          await updateOnboardingWorker({
            stage: ONBOARDING_STAGES.ADDRESS,
            address: place.address,
            location: place.location,
            signupFlowVersion: "V2",
            tmz,
          });

          // We refetch so things like MSA which is computed in backend is also updated
          await refetchWorker();

          logEvent(APP_V2_USER_EVENTS.ONBOARDING_STEP_COMPLETED, {
            // Address is PII, so we don't need to log it
            stepCompleted: ONBOARDING_STAGES.ADDRESS,
          });

          await onNext();
        } catch (error) {
          logError(APP_V2_APP_EVENTS.ONBOARDING_UPDATE_ADDRESS_ERROR, {
            error,
            metadata: {
              workerId: worker?.userId,
            },
          });
          showErrorToast(ADDRESS_ERROR_MESSAGES.UNEXPECTED_ERROR);
        }
      },
    });

  const handleSave = async () => {
    await updateOnboardingAddress();
  };

  return {
    place,
    loadingData,
    handleUseCurrentLocation,
    addressInputModalState,
    handleSave,
    isUpdatingOnboardingAddress,
    isLoadingWorker,
    addressInput,
    handleAddressInputChange,
    handlePlaceChange,
    handleManualEntry,
    addressInputErrorMessage,
    isEnablingGeoLocation,
  };
}
